/// <reference lib="dom"/>
import {
  type ChiScope,
  createContext,
  type Deps,
  useContext,
  useMemo,
  useRef,
  useState,
} from "@chi/chi/hooks";
import { NoiseBuffer, type NoiseBufferProps } from "@/noise.tsx";

export interface WbaxEnv {
  ctx: BaseAudioContext;
  destination: AudioNode;
  getNode<N extends AudioNode = AudioNode>(id: string): N | undefined;
}

export const WbaxEnvContext = createContext<WbaxEnv | null>(null);
export const WbaxTimeContext = createContext<number>(0);

export function ScopeValue<T>(scope: ChiScope | null, context: { defaultValue: T }): T {
  for (let s = scope; s; s = s.parent) {
    if (s.context === context) return s.value as T;
  }
  return context.defaultValue;
}

export function useWbax(): WbaxEnv {
  const env = useContext(WbaxEnvContext);
  if (!env) throw new Error("wbax: hooks can only be used inside a WbaxRoot");
  return env;
}

export function useAudioCtx(): BaseAudioContext {
  return useWbax().ctx;
}

export function useTime(): number {
  return useContext(WbaxTimeContext);
}

let idSeq = 0;
export function useId(prefix = "wbax"): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) ref.current = `${prefix}${++idSeq}`;
  return ref.current;
}

export function useBuffer(make: (ctx: BaseAudioContext) => AudioBuffer, deps: Deps): AudioBuffer {
  const ctx = useAudioCtx();
  return useMemo(() => make(ctx), [ctx, ...deps]);
}

export function useNoiseBuffer(props: NoiseBufferProps = {}): AudioBuffer {
  return useBuffer(
    (ctx) => NoiseBuffer(ctx, props),
    [props.type, props.duration, props.channels, props.rng],
  );
}

export interface WbaxVoice<T> {
  key: number;
  time: number;
  data: T;
}

export interface WbaxVoices<T> {
  voices: WbaxVoice<T>[];
  trigger: (data: T, time?: number) => number;
  release: (key: number) => void;
  clear: () => void;
}

export function useVoices<T = undefined>(limit = 64): WbaxVoices<T> {
  const ctx = useAudioCtx();
  const [voices, setVoices] = useState<WbaxVoice<T>[]>([]);
  const seq = useRef(0);

  const api = useMemo(() => ({
    trigger: (data: T, time?: number) => {
      const key = ++seq.current;
      const voice = { key, time: time ?? ctx.currentTime, data };
      setVoices((list) => [...list, voice].slice(-limit));
      return key;
    },
    release: (key: number) => setVoices((list) => list.filter((v) => v.key !== key)),
    clear: () => setVoices([]),
  }), [ctx, limit]);

  return { voices, ...api };
}
